/*
 * Clinic-local dates and wall-clock times. Schedule shows and books in the clinic's time zone,
 * never the browser's, so every conversion names the zone.
 */

/** The clinic-local calendar date (YYYY-MM-DD) of an instant. */
export function dateKeyInTimeZone(value: string | Date, timeZone: string): string {
  const date = typeof value === "string" ? new Date(value) : value;
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((candidate) => candidate.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

/** A calendar date moved by whole days. Dates only, so no time zone applies. */
export function addDays(date: string, amount: number): string {
  const parsed = new Date(`${date}T12:00:00Z`);
  parsed.setUTCDate(parsed.getUTCDate() + amount);
  return parsed.toISOString().slice(0, 10);
}

/**
 * The instant a clinic-local date and wall-clock time name. A time skipped when the clocks go
 * forward lands the same distance past the change (02:30 becomes 03:30); a time that happens
 * twice when they go back is its first occurrence.
 */
export function localDateTimeToIso(date: string, time: string, timeZone: string): string {
  const [year, month, day] = date.split("-").map(Number);
  const [hour, minute] = time.split(":").map(Number);
  if (
    year === undefined ||
    month === undefined ||
    day === undefined ||
    hour === undefined ||
    minute === undefined ||
    [year, month, day, hour, minute].some((value) => !Number.isInteger(value))
  ) {
    throw new Error("Choose a valid local date and time.");
  }
  const target = Date.UTC(year, month - 1, day, hour, minute, 0);
  const wallClock = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  let instant = target;
  // Three corrections settle any offset; inside a skipped hour they end past the change.
  for (let iteration = 0; iteration < 3; iteration += 1) {
    const rendered = wallClock.formatToParts(new Date(instant));
    const number = (type: Intl.DateTimeFormatPartTypes) =>
      Number(rendered.find((candidate) => candidate.type === type)?.value ?? 0);
    const wall = Date.UTC(
      number("year"),
      number("month") - 1,
      number("day"),
      number("hour"),
      number("minute"),
    );
    instant += target - wall;
  }
  return new Date(instant).toISOString();
}

/**
 * Whole clinic-local days: from the start of startDate to the start of the day after endDate.
 * The end is the next midnight, so nothing is left open before it, and a day the clocks change
 * lasts 23 or 25 hours.
 */
export function allDayRange(
  startDate: string,
  endDate: string,
  timeZone: string,
): { readonly startAt: string; readonly endAt: string } {
  return {
    startAt: localDateTimeToIso(startDate, "00:00", timeZone),
    endAt: localDateTimeToIso(addDays(endDate, 1), "00:00", timeZone),
  };
}

/**
 * An instant on the clinic's clock, never the browser's: `{dateStyle: "medium", timeStyle:
 * "short"}` gives "Oct 8, 2026, 9:30 a.m." in the clinic's zone.
 */
export function formatInClinic(
  instant: string,
  timeZone: string,
  options: Intl.DateTimeFormatOptions,
): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone, ...options }).format(new Date(instant));
}

/**
 * A calendar date (YYYY-MM-DD) as people read it ("Oct 8, 2026"). It is already a clinic-local
 * date, so it is formatted at noon UTC and no time zone can move it to another day.
 */
export function formatCalendarDate(date: string): string {
  const parsed = new Date(`${date}T12:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return date;
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "UTC",
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(parsed);
}
